// Cores da loja (tema). Configuradas por variáveis REACT_APP_COLOR_* no .env, junto com
// REACT_APP_COMPANY_CODE (ver .env.example). A API não tem campos de cor: o tema é por deploy.
//
// Como toda variável do CRA, só são lidas no `yarn start`/`yarn build`: depois de trocar,
// reinicie o dev server ou gere o build de novo.
//
// Cada cor vira uma variável CSS com os canais RGB ("182 28 37"), usada pelo Tailwind
// (tailwind.config.js) como `rgb(var(--color-x) / <alpha-value>)` — assim `bg-brand-600/30` funciona.
// Tons claros/escuros (brand-50…800, cream-*, warm-*) são derivados das cores base.

// Bonfim Gourmet — usada quando a variável não existe ou é inválida
export const THEME_DEFAULTS = {
  primary: "#B61C25", // botões, links, preço no rodapé da sacola (brand-600)
  primaryDark: "#8E141B", // hover dos botões, barra de aviso do topo (brand-700)
  highlight: "#D9383A", // pílula de categoria ativa, preço sobre a foto (brand-500)
  accent: "#FBBF24", // selo "Aberto agora", contador da sacola, selo do banner
  background: "#FCF9F4", // fundo da página (cream)
  text: "#4A281E", // títulos e textos principais (cocoa)
  textMuted: "#736357", // descrições e textos secundários (cocoa-muted)
  footer: "#11100F", // fundo do rodapé (warm-950)
  success: "#2E7D32", // selo de estoque "N disponíveis"
};

// Nome da variável de ambiente de cada cor
const ENV = {
  primary: process.env.REACT_APP_COLOR_PRIMARY,
  primaryDark: process.env.REACT_APP_COLOR_PRIMARY_DARK,
  highlight: process.env.REACT_APP_COLOR_HIGHLIGHT,
  accent: process.env.REACT_APP_COLOR_ACCENT,
  background: process.env.REACT_APP_COLOR_BACKGROUND,
  text: process.env.REACT_APP_COLOR_TEXT,
  textMuted: process.env.REACT_APP_COLOR_TEXT_MUTED,
  footer: process.env.REACT_APP_COLOR_FOOTER,
  success: process.env.REACT_APP_COLOR_SUCCESS,
};

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** "#abc" | "abc" | "#aabbcc" → [r, g, b]; null se inválido */
const hexToRgb = (hex) => {
  const m = String(hex || "").trim().match(HEX_RE);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};

/** Mistura duas cores RGB: t = 0 → a, t = 1 → b */
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));

const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];

/** Luminância relativa (WCAG) */
const luminance = ([r, g, b]) => {
  const c = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};

const contrast = (a, b) => {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
};

/** Resolve as cores base (env → padrão), avisando no console sobre valores inválidos */
const resolveBaseColors = () => {
  const colors = {};
  Object.keys(THEME_DEFAULTS).forEach((key) => {
    const fromEnv = ENV[key] && String(ENV[key]).trim();
    const parsed = fromEnv ? hexToRgb(fromEnv) : null;
    if (fromEnv && !parsed) {
      console.error(
        `❌ Cor inválida em REACT_APP_COLOR_* (${key} = "${fromEnv}"): use hexadecimal, ex. #B61C25. Usando o padrão.`
      );
    }
    colors[key] = parsed || hexToRgb(THEME_DEFAULTS[key]);
  });
  return colors;
};

/** Monta o mapa { "--color-x": "r g b" } com as cores base e os tons derivados */
export const buildThemeVariables = () => {
  const c = resolveBaseColors();

  // Texto branco sobre o botão precisa de contraste (WCAG AA ≥ 4.5)
  if (contrast(c.primary, WHITE) < 4.5) {
    console.warn(
      "⚠️ REACT_APP_COLOR_PRIMARY é clara demais para texto branco nos botões (contraste < 4.5:1)."
    );
  }

  const vars = {
    "--color-brand-50": mix(c.primary, WHITE, 0.94),
    "--color-brand-100": mix(c.primary, WHITE, 0.88),
    "--color-brand-200": mix(c.primary, WHITE, 0.75),
    "--color-brand-300": mix(c.primary, WHITE, 0.5),
    "--color-brand-500": c.highlight,
    "--color-brand-600": c.primary,
    "--color-brand-700": c.primaryDark,
    "--color-brand-800": mix(c.primaryDark, BLACK, 0.25),
    "--color-accent": c.accent,
    // Texto sobre o accent: escuro ou branco, o que tiver mais contraste
    "--color-on-accent":
      contrast(c.accent, [28, 25, 23]) >= contrast(c.accent, WHITE)
        ? [28, 25, 23]
        : WHITE,
    "--color-cream": c.background,
    "--color-cream-50": mix(c.background, WHITE, 0.5),
    "--color-cream-100": mix(c.background, c.text, 0.06),
    "--color-cream-200": mix(c.background, c.text, 0.1),
    "--color-cream-300": mix(c.background, c.text, 0.08),
    "--color-cocoa": c.text,
    "--color-cocoa-muted": c.textMuted,
    "--color-warm-800": mix(c.footer, WHITE, 0.09),
    "--color-warm-900": mix(c.footer, WHITE, 0.04),
    "--color-warm-950": c.footer,
    "--color-success": c.success,
    "--color-success-light": mix(c.success, WHITE, 0.9),
  };

  return Object.fromEntries(
    Object.entries(vars).map(([k, rgb]) => [k, rgb.join(" ")])
  );
};

/**
 * Aplica o tema no <html> (variáveis CSS) e na cor da barra do navegador (meta theme-color).
 * Deve rodar antes do primeiro render (src/index.js).
 */
export const applyTheme = () => {
  const vars = buildThemeVariables();
  const root = document.documentElement;
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", `rgb(${vars["--color-cream"]})`);
};
